const DB_NAME = "akin_recordings";
const STORE_NAME = "pending_uploads";

export const MAX_AUTO_RETRIES = 3;

export interface StoredRecording {
	blob: Blob;
	failCount: number;
}

function openDB(): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const req = indexedDB.open(DB_NAME, 1);
		req.onupgradeneeded = () => {
			req.result.createObjectStore(STORE_NAME);
		};
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
}

// Saves a blob and resets the fail count to 0 (new recording = fresh start).
export async function saveRecording(key: string, blob: Blob): Promise<void> {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, "readwrite");
		const req = tx.objectStore(STORE_NAME).put({ blob, failCount: 0 }, key);
		req.onsuccess = () => resolve();
		req.onerror = () => reject(req.error);
	});
}

export async function getRecording(
	key: string,
): Promise<StoredRecording | null> {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, "readonly");
		const req = tx.objectStore(STORE_NAME).get(key);
		req.onsuccess = () => resolve((req.result as StoredRecording) ?? null);
		req.onerror = () => reject(req.error);
	});
}

export async function deleteRecording(key: string): Promise<void> {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, "readwrite");
		const req = tx.objectStore(STORE_NAME).delete(key);
		req.onsuccess = () => resolve();
		req.onerror = () => reject(req.error);
	});
}

async function incrementFailCount(key: string): Promise<number> {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const tx = db.transaction(STORE_NAME, "readwrite");
		const store = tx.objectStore(STORE_NAME);
		const getReq = store.get(key);
		getReq.onsuccess = () => {
			const existing = getReq.result as StoredRecording | undefined;
			if (!existing) {
				resolve(0);
				return;
			}
			const updated: StoredRecording = {
				...existing,
				failCount: existing.failCount + 1,
			};
			const putReq = store.put(updated, key);
			putReq.onsuccess = () => resolve(updated.failCount);
			putReq.onerror = () => reject(putReq.error);
		};
		getReq.onerror = () => reject(getReq.error);
	});
}

/**
 * Attempts to upload a recording already persisted in IDB under `key`.
 * Retries silently up to MAX_AUTO_RETRIES total failures (cumulative across sessions).
 *
 * Returns:
 *   "success"   — upload succeeded, IDB entry deleted
 *   "exceeded"  — too many cumulative failures, needs manual retry
 *   "not_found" — no pending recording in IDB for this key
 */
export async function attemptUpload(
	key: string,
	doUpload: (blob: Blob) => Promise<unknown>,
): Promise<"success" | "exceeded" | "not_found"> {
	const stored = await getRecording(key);
	if (!stored) return "not_found";
	if (stored.failCount >= MAX_AUTO_RETRIES) return "exceeded";

	const attemptsLeft = MAX_AUTO_RETRIES - stored.failCount;

	for (let i = 0; i < attemptsLeft; i++) {
		if (i > 0) {
			await new Promise<void>((r) => setTimeout(r, i * 1500));
		}
		try {
			await doUpload(stored.blob);
			await deleteRecording(key);
			return "success";
		} catch {
			const newCount = await incrementFailCount(key);
			if (newCount >= MAX_AUTO_RETRIES) return "exceeded";
		}
	}

	return "exceeded";
}
